import React, { useEffect, useState, useRef } from "react";
import logoImage from "../../Image/logo-blue.png";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { PiUserSwitchFill } from "react-icons/pi";
import styles from "./Sidebar.module.scss";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import licenseEdit from "../../Image/license-edit.gif";
import license from "../../Image/license.gif";
import {
  setLocation,
  setItems,
  SidebarItem,
  setOpen,
} from "../../Redux/Sidebar/sidebarSlice";
import { isMobileDevice } from "../../Utils/Utils";
import { clearAccess } from "../../Redux/Access/accessSlice";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Tour,
  Button,
  Tooltip,
  message,
  Checkbox,
  notification as notificationAntd,
} from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import type { TourProps } from "antd";
import { MdLiveHelp } from "react-icons/md";
import { RiAdminFill } from "react-icons/ri";
import { mainItems } from "./SidebarItems";
import { FiHelpCircle, FiLogOut, FiMenu } from "react-icons/fi";
import LoginFunction from "../../Functions/Login/Login";
import { clearUser } from "../../Redux/User/userSlice";
import { openCustomersModal } from "../../Redux/SuperAdminModal/modalSlice";
import { clearCustomer } from "../../Redux/Customer/customerSlice";
import IdleLogout from "../IdleLogout/IdleLogout";

type RefItems = {
  label: string;
  ref?: React.RefObject<HTMLLIElement>;
};


const Sidebar: React.FC = () => {
  const ADMIN_PORTAL_URL = process.env.REACT_APP_ADMIN_PORTAL_URL;
  const userInfo = useSelector((state: RootState) => state.user);
  const sessionToken = useSelector((state: RootState) => state.user.token);
  const isCustomerModalOpen = useSelector(
    (state: RootState) => state.customersModal.isOpen
  );
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );

  const userID = userInfo?.user?.id;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [startSidebarTour, setStartSidebarTour] = useState<boolean>(false);

  const [dontShowTour, setDontShowTour] = useState(false);
  const [storedSession, setStoredSession] = useState<string | null>();

  const [api, contextHolderNotification] = notificationAntd.useNotification();
  const isUserAdmin = userInfo.user?.loginType === "admin";
  const [isCustomerModalClosedOnce, setIsCustomerModalClosedOnce] =
    useState(false);

  // Refs for use in Tour Component
  const userManagementRef = useRef(null);
  const settingsRef = useRef(null);
  const licenseOverviewRef = useRef(null);

  // tour button styles
  const prevButtonStyle = {
    fontSize: "16px",
    height: "30px",
    paddingLeft: "20px",
    paddingRight: "20px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    margin: "2px",
  };

  const nextButtonStyle = {
    ...prevButtonStyle,
    backgroundColor: "#074481",
    color: "white",
  };
  // Load checkbox state from local storage on component mount
  useEffect(() => {
    const dontShowAgain = localStorage.getItem(`dontShowTourAgain_${userID}`);
    const storedSession = localStorage.getItem(`storedSession_${userID}`);

    setStoredSession(storedSession);
    //triggerOpenMenu("Connect");

    if (dontShowAgain === "true") {
      setDontShowTour(true);
      setStartSidebarTour(false);
    } else if (sessionToken !== storedSession && isCustomerModalClosedOnce) {
      setStartSidebarTour(true);
    }
  }, [isCustomerModalClosedOnce]);

  useEffect(() => {
    if (!isCustomerModalOpen && !isCustomerModalClosedOnce) {
      setIsCustomerModalClosedOnce(true);
    }
  }, [isCustomerModalOpen]);

  // Function to handle checkbox change
  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;

    setDontShowTour(checked);
    // Store checkbox state in local storage with the user identifier
    localStorage.setItem(`dontShowTourAgain_${userID}`, checked.toString());
  };

  const handleCloseTour = () => {
    localStorage.setItem(`storedSession_${userID}`, sessionToken!);
  };

  const handleOpenTour = () => {
    setStartSidebarTour(true);
  };

  const reduxSidebarItems = useSelector(
    (state: RootState) => state.sidebar.items
  );

  const setSidebarItems = (items: SidebarItem[]) => {
    dispatch(setItems(items));
  };

  const otherItems: SidebarItem[] = [
    { label: "Help", icon: FiHelpCircle, route: "/help" },
    { label: "Version", icon: FiMenu, route: "/version" },
    { label: "Log out", icon: FiLogOut, route: "/logout" },
  ];

  const location = useLocation();
  const isOpen = useSelector((state: RootState) => state.sidebar.isOpen);

  const title = mainItems.concat(otherItems).map((item) => {
    if (item.children) {
      for (let child of item.children) {
        if (child.route && window.location.pathname.includes(child?.route))
          return child.label;
      }
    } else if (item.route && window.location.pathname.includes(item?.route))
      return item.label;
  });
  const [messageApi, contextHolder] = message.useMessage();
  const user = useSelector((state: RootState) => state.user);

  //window resize
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });


  let refSidebarItems = new Map<string, any>();

  const refItems: RefItems[] = [
    {
      label: "User Management",
      ref: userManagementRef,
    },
    {
      label: "Settings",
      ref: settingsRef,
    },
    {
      label: "License Overview",
      ref: licenseOverviewRef,
    },
  ];

  useEffect(() => {
    const filteredMainItems = mainItems.filter(
      (item) => !item.superOnly || user?.user?.loginType === "super"
    );
    setSidebarItems(filteredMainItems);

    reduxSidebarItems.forEach((item) => {
      refItems.forEach((refItem) => {
        if (item.label === refItem.label) {
          refSidebarItems.set(item.label, refItem?.ref);
        }
      });
    });
  }, [user, userManagementRef, settingsRef, licenseOverviewRef]);

  const steps: TourProps["steps"] = [
    {
      nextButtonProps: {
        style: nextButtonStyle,
      },
      title: "",
      cover: (
        <div>
          <img
            alt="tour.png"
            src={logoImage}
            style={{ width: "100px", height: "auto", marginBottom: "15px" }}
          />
          <p
            className="d-flex justify-content-left mb-2"
            style={{ fontWeight: "600" }}
          >
            Welcome to the Admin Portal!
          </p>
          <div className="d-flex justify-content-start mb-2">
            <p style={{ textAlign: "left" }}>
              We're excited to have you on board. This tour will guide you
              through the portal's features and help you get familiar with the
              interface.
            </p>
          </div>
          <p style={{ textAlign: "left" }} className="mb-4">
            Click 'Next' to begin the tour or click{" "}
            <Button type="dashed" size="small">
              X
            </Button>{" "}
            to skip it and explore on your own.
          </p>
          <div style={{ textAlign: "left" }}>
            <Checkbox
              checked={dontShowTour}
              onChange={handleCheckboxChange}
              style={{
                fontWeight: "600",
              }}
            >
              Do not show this tour again.
            </Checkbox>
          </div>
        </div>
      ),
      placement: "center",
    },
    {
      nextButtonProps: {
        style: nextButtonStyle,
      },
      prevButtonProps: {
        style: prevButtonStyle,
      },
      title: "User Management",
      description:
        "Here you can view all the licenses assigned to a chosen customer. You can also view in depth details of each license and manage user access to features and services.",
      target: userManagementRef?.current,
      placement: "right",
      cover: (
        <img
          alt="license.gif"
          src={license}
          style={{ width: "100%", height: "auto", marginBottom: "15px" }}
        />
      ),
    },
    {
      nextButtonProps: {
        style: nextButtonStyle,
      },
      prevButtonProps: {
        style: prevButtonStyle,
      },
      title: "License Management",
      description:
        "View, assign, and manage licenses for users. You can also view license details and manage user access to features and services.",
      placement: "center",
      cover: (
        <img
          alt="license-edit.gif"
          src={licenseEdit}
          style={{ width: "100%", height: "auto", marginBottom: "15px" }}
        />
      ),
    },
    {
      nextButtonProps: {
        style: nextButtonStyle,
      },
      prevButtonProps: {
        style: prevButtonStyle,
      },
      title: "License Overview",
      description:
        "View all the licenses assigned to a chosen customer. You can also view in depth details of each license and manage user access to features and services.",
      target: licenseOverviewRef?.current,
      placement: "right",
    },
    {
      nextButtonProps: {
        style: nextButtonStyle,
      },
      prevButtonProps: {
        style: prevButtonStyle,
      },
      title: "Settings",
      description:
        "Manage your settings, configure your account, and customize your preferences.",
      target: settingsRef?.current,
      placement: "right",
    },
  ];
  const filteredSteps = steps.filter(
    (step) => step.target !== null || step.target === undefined
  );

  // functio to open menu items for tour
  const triggerOpenMenu = (label: string) => {
    const itemToOpen = reduxSidebarItems.find((item) => item.label === label);
    if (itemToOpen?.children && itemToOpen.showChildren === false) {
      const updatedItems = reduxSidebarItems?.map((i) => ({
        ...i,
        showChildren: i === itemToOpen ? !i.showChildren : false, // Close all others, open the clicked one
      }));
      setSidebarItems(updatedItems);
    }
  };

  // @ts-ignore
  useEffect(() => {
    function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }

    window.addEventListener("resize", handleResize);
    return (_: any): void => {
      window.removeEventListener("resize", handleResize);
    };
  });
  const toggleSidebar = () => {
    dispatch(setOpen(!isOpen));
  };

  useEffect(() => {
    dispatch(setLocation(location.pathname));
  }, [location]);

  const handleLogout = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.preventDefault();
    LoginFunction.logout(accessToken!!, sessionToken!!).then((data) => {});
    dispatch(clearAccess(null));
    dispatch(clearUser(null));
    dispatch(clearAccess(null));
    dispatch(clearCustomer());
    navigate("/");
  };

  const success = () => {
    messageApi.open({
      type: "success",
      content:
        "Thank you for subscribing! You have successfully signed up for the web push meeting events.",
    });
  };

  const error = () => {
    messageApi.open({
      type: "error",
      content:
        "Unfortunately, an error occurred during your subscription process for the web push meeting events. Please try again or contact our support if the problem persists",
    });
  };

  const successUnsub = () => {
    messageApi.open({
      type: "success",
      content:
        "You have successfully unsubscribe from the web push meeting events.",
    });
  };

  const errorUnsub = () => {
    messageApi.open({
      type: "error",
      content:
        "Unfortunately, an error occurred during your unsubscription process for the web push meeting events. Please try again or contact our support if the problem persists",
    });
  };

  const handleChangePage = (route: string) => {
    navigate(route);
  };

  const renderSidebarItem = (item: SidebarItem, index: number) => {
    const isActive = window.location.pathname.includes(item.route!);
    const activeClass = isActive ? styles.active : "";
    const currentRef = refItems.find((ref) => ref.label === item.label)?.ref;

    return (
      <li
        key={index}
        className={`${styles.sidebarItem} ${activeClass}`}
        ref={currentRef}
      >
        {contextHolder}
        <div className={`${styles.sidebarItemWrapper}`}>
          {item.label === "Log out" ? (
            <span
              onClick={handleLogout}
              style={{ cursor: "pointer" }}
              className="d-flex align-items-center text-white"
            >
              <item.icon />
              <div
                className={`${styles.itemText} ${isOpen ? styles.show : ""}`}
              >
                {item.label}
              </div>
            </span>
          ) : (
            <a
              onClick={(e) => {
                e.preventDefault();

                if (item.children) {
                  const updatedItems = reduxSidebarItems?.map((i) => ({
                    ...i,
                    showChildren: i === item ? !i.showChildren : false, // Close all others, open the clicked one
                  }));
                  setSidebarItems(updatedItems);
                } else {
                  handleChangePage(item?.route ?? "");
                }
              }}
              style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
            >
              <item.icon />
              <div
                className={`${styles.itemText} ${
                  item.showChildren ? styles.show : ""
                }`}
              >
                {item.label}
              </div>
              {item.children && isOpen && (
                <div>
                  {item.showChildren ? (
                    <MdKeyboardArrowUp />
                  ) : (
                    <MdKeyboardArrowDown />
                  )}
                </div>
              )}
            </a>
          )}
        </div>
      </li>
    );
  };

  //renderSubItem

  const renderSubItem = (item: SidebarItem, index: number) => {
    const isActive = window.location.pathname.includes(item?.route ?? "");
    const activeClass = isActive ? styles.active : "";

    const currentRef = refItems.find((ref) => ref.label === item.label)?.ref;

    return (
      <li
        key={index}
        className={`${styles.sidebarItem} ${activeClass}`}
        ref={currentRef}
        style={{ background: "#134e88" }}
      >
        {contextHolder}
        <div className={`${styles.sidebarItemWrapper}`}>
          <a
            onClick={(e) => {
              e.preventDefault();
              handleChangePage(item?.route ?? "");
            }}
            style={
              isOpen
                ? { cursor: "pointer", marginLeft: "20px" }
                : { cursor: "pointer" }
            }
          >
            <item.icon />
            <div className={`${styles.itemText} ${isOpen ? styles.show : ""}`}>
              {item.label}
            </div>
          </a>
        </div>
      </li>
    );
  };

  // handle open customers modal

  const handleOpenCustomersModal = () => {
    dispatch(openCustomersModal());
  };
  const userData = useSelector((state: RootState) => state.user);
  const customerData = useSelector((state: RootState) => state.customer);
  return (
    <div>
      <IdleLogout />
      {contextHolderNotification}
      <Tour
        open={startSidebarTour}
        onClose={() => {
          setStartSidebarTour(false);
          handleCloseTour();
        }}
        steps={filteredSteps}
      />

      <div className={`${styles.container} d-flex`}>
        <div
          className={`${styles.header} ${isOpen ? styles["open"] : ""}${
            isMobileDevice() ? styles["mobile"] : ""
          }`}
        >
          <div className="row w-100 d-flex justify-content-between align-items-center">
            <h6 className="flex-grow-1 col-8 text-center my-auto ">{title}</h6>
            <div className="col-1 my-auto d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                {isUserAdmin && !isMobileDevice() && (
                  <span className="mr-2 text-nowrap text-truncate">
                    {customerData?.name}
                  </span>
                )}
                {isUserAdmin && (
                  <Tooltip title="Open Admin Portal" placement="left">
                    <a href={ADMIN_PORTAL_URL} target="_blank" rel="noreferrer">
                      <Button
                        size="small"
                        type="primary"
                        className="btn-wiline align-items-center m-1"
                        style={{
                          backgroundColor: "white",
                          boxShadow: "none",
                        }}
                        icon={
                          <RiAdminFill style={{ color: "#074481" }} size={20} />
                        }
                      ></Button>
                    </a>
                  </Tooltip>
                )}
              </div>

              {userData?.user?.loginType === "super" && (
                <div className="d-flex align-items-center">
                  {!isMobileDevice() && (
                    <span className="mr-2 text-nowrap text-truncate">
                      {customerData?.name}
                    </span>
                  )}
                  <Tooltip title="Change customer" placement="left">
                    <Button
                      size="small"
                      type="primary"
                      style={{
                        backgroundColor: "white",
                        borderRadius: "15px",
                        boxShadow: "none",
                      }}
                      className="btn-wiline align-items-center m-1"
                      icon={
                        <PiUserSwitchFill
                          className="mb-1"
                          style={{ color: "#074481" }}
                          size={20}
                        />
                      }
                      onClick={handleOpenCustomersModal}
                    ></Button>
                  </Tooltip>
                </div>
              )}
              <Tooltip title="Tour guide" placement="left">
                <Button
                  size="small"
                  type="primary"
                  style={{
                    backgroundColor: "white",
                    borderRadius: "15px",
                    boxShadow: "none",
                  }}
                  className="btn-wiline align-items-center m-1"
                  icon={
                    <MdLiveHelp
                      className="mb-1"
                      style={{ color: "#074481" }}
                      size={20}
                    />
                  }
                  onClick={handleOpenTour}
                ></Button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles.sidebar} ${isOpen ? styles["open"] : ""} ${
          isMobileDevice() ? styles["mobile"] : ""
        }`}
      >
        <div className={`${styles.sidebarHeader} mt-2 mb-2`}>
          <div
            className={`${styles.sidebarLogo} ${isOpen ? styles["open"] : ""}`}
          >
            <div
              className={`${styles.sidebarLogoImg} ${
                isOpen ? styles["open"] : ""
              }`}
            ></div>
          </div>
          <div
            className={`${styles.sidebarToggle} ${
              isMobileDevice() ? styles["mobile"] : ""
            } ${isOpen ? styles["open"] : ""}`}
            onClick={toggleSidebar}
          >
            {isOpen ? <IoIosArrowBack /> : <IoIosArrowForward />}
          </div>
        </div>
        {isOpen && (
          <h4 className={`d-flex justify-content-center text-white`}>
            Admin Portal
          </h4>
        )}
        <ul className={`${styles.sidebarMenu}`}>
          {reduxSidebarItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.children ? (
                <>
                  {renderSidebarItem(item, index)}

                  {item.children &&
                    item.showChildren &&
                    item.children.map((childItem, childIndex) =>
                      renderSubItem(childItem, childIndex)
                    )}
                </>
              ) : (
                renderSidebarItem(item, index)
              )}
            </React.Fragment>
          ))}
        </ul>
        <ul className={`${styles.sidebarMenu} ${styles["sidebarMenu-bottom"]}`}>
          {otherItems.map((item, index) => renderSidebarItem(item, index))}
        </ul>
      </div>
    </div>
  );
};
export default Sidebar;
